/**
 * RepBuddy - Billing / Account service
 *
 * Single source of truth for the Account Management page. Everything here now comes from
 * the backend, which reads it from Stripe and from our own subscription projection.
 *
 * It did not used to. This module previously DERIVED the whole page from Supabase
 * user_metadata plus a localStorage "intent store": the price came from a hardcoded table,
 * the renewal date from a global launch constant, the card list was empty behind a "coming
 * soon" button, and invoices were generated in the browser as a .txt file. Cancelling wrote
 * a flag to localStorage and changed nothing. The page looked like working billing and was
 * made of placeholders, which is worse than an empty state because it cannot be told apart
 * from the real thing.
 *
 * The shape returned by fetchBilling() is unchanged, so the section components did not have
 * to move. What changed is where each field comes from:
 *
 *   subscription   /api/billing/subscription  (our projection of the Stripe subscription)
 *   paymentMethods /api/billing/subscription  (Stripe, read-only: brand, last4, expiry)
 *   invoices       /api/billing/subscription  (Stripe, with hosted + PDF links)
 *   usage/credits  /api/credits               (live meters, the authoritative balances)
 *   trial          /api/billing/subscription  (the PER-USER window, not a global constant)
 *   offers         /api/billing/subscription  (this user's price book, so a founding
 *                                              member sees founding rates for upgrades)
 *
 * Mutations are no longer local. Plan change, interval change, cancel and resume all go to
 * the Stripe Customer Portal, which is the only place they can actually happen.
 */


import { METERS, PLAN_KEYS, planRows, allowanceFor, formatNumber as formatMeterNumber } from '../lib/meters';
import { fetchSubscription, portalAndRedirect } from './stripeBilling';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ─── Launch / trial configuration (matches the Register + Founding pages) ─────
// Public trial: "7-day free trial · no credit card required · starts the day the account is live."
export const LAUNCH_DATE = '2026-07-28T00:00:00Z';
export const TRIAL_DAYS = 7;
export const REQUIRES_CARD_FOR_TRIAL = false;

// ─── Plan catalogue ──────────────────────────────────────────────────────────
// There is deliberately NO price table here any more. Two hardcoded tables used to live at
// this spot (PUBLIC_PRICING and FOUNDING_PRICING) and they were wrong in a way that was
// hard to see: public yearly was stored as 49, a PER-MONTH rate, while Stripe charges $490
// once a year. The Account page rendered "$49/mo" for a plan that bills $490, and nothing
// connected the two numbers so nothing could catch the drift.
//
// Prices now come from `offers` on the billing model, which the backend resolves from the
// caller's own price book. See priceFor below.

export { PLAN_KEYS };

// Human "what a plan includes" line for one meter on one plan, e.g. "150 verified leads / mo"
// or "Unlimited AI drafting + chat". Numbers come from the catalogue (allowanceFor) so the
// plan picker and marketing can never drift from what the backend actually grants.
const FEATURE_PHRASE = {
  leads:   (v) => `${formatMeterNumber(v)} verified leads / mo`,
  verify:  (v) => `${formatMeterNumber(v)} email verifications / mo`,
  enrich:  (v) => `${formatMeterNumber(v)} company enrichments / mo`,
  website: (v) => `${formatMeterNumber(v)} website lookups / mo`,
  ai:      () => 'Unlimited AI drafting + chat',
};
function meterFeatureLines(plan) {
  return METERS.map((m) => FEATURE_PHRASE[m.key](allowanceFor(m.key, plan)));
}

// Headline features per plan (used by the plan picker so the account page and marketing
// stay consistent). The metered lines come from the catalogue; Pro appends its extras.
export const PLAN_FEATURES = {
  Solo: meterFeatureLines('Solo'),
  Pro: [
    ...meterFeatureLines('Pro'),
    'Competitor discovery + buying-signal hunts',
    'Priority support',
  ],
};

// Structured monthly allowances per plan. Drives the Subscription "Usage & limits" card and
// the Account usage meters. Derived from the shared catalogue (lib/meters) so every surface
// - pricing page, account page, in-product cost copy - reads the same numbers. `key` maps a
// plan row to its live server meter (see fetchBilling): leads → user_credits;
// verify/enrich/website → usage_meters; ai is never metered.
export const PLAN_LIMITS = {
  Solo: planRows('Solo'),
  Pro: planRows('Pro'),
};

// ─── Formatting helpers ───────────────────────────────────────────────────────
export function formatDate(iso) {
  if (!iso) return '-';
  try {
    // Format in UTC so calendar dates (launch, billing) don't drift a day in
    // timezones behind UTC - e.g. 2026-07-28T00:00Z must read "Jul 28", not "Jul 27".
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
    });
  } catch { return '-'; }
}

export function formatMoney(amount, currency = '$') {
  if (amount == null) return '-';
  return `${currency}${Number(amount).toFixed(2).replace(/\.00$/, '')}`;
}

export function daysUntil(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));
}

/** Short relative label for a recent timestamp (e.g. "just now", "3 min ago"). */
export function timeAgo(iso) {
  if (!iso) return '-';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return formatDate(iso);
}

/** Thousands-separated number, or passthrough for strings like "Unlimited". */
export function formatNumber(n) {
  return typeof n === 'number' ? n.toLocaleString('en-US') : n;
}

/**
 * Stripe-style renewal descriptor for the current subscription state. Returns the
 * label + date the UI should show so Billing and Subscription stay consistent:
 *   canceled           → "Cancels on"
 *   on trial (pre/live) → "First billing on"
 *   active (post-trial) → "Renews on"
 */
export function renewalInfo(sub, trial) {
  if (!sub) return { label: '-', date: null };
  // A trial date is only ever right for someone still ON the trial. This used to read
  // `trial?.endsAt || sub.nextBillingDate` for a cancelled plan, which preferred the trial
  // end unconditionally: a paying customer whose trial lapsed weeks earlier was told their
  // access ended on a date already in the past.
  const onTrial = trial?.status === 'pending' || trial?.status === 'active';
  if (sub.canceled) {
    return { label: 'Cancels on', date: sub.periodEnd || sub.cancelsAt || (onTrial ? trial.endsAt : null) };
  }
  if (onTrial) return { label: 'First billing on', date: sub.billingStartsAt || trial.endsAt };
  return { label: 'Renews on', date: sub.periodEnd || sub.nextBillingDate };
}

/**
 * Price descriptor for a plan + billing cycle, mirroring how it's shown at signup.
 * Founding annual is a yearly total (/yr); everything else is a per-month rate (/mo).
 */
export function priceFor(offers, plan, billing) {
  const itemKey = `${String(plan || '').toLowerCase()}_${billing === 'Annual' ? 'yearly' : 'monthly'}`;
  const offer = offers?.[itemKey];
  if (!offer) return { amount: null, unit: billing === 'Annual' ? '/yr' : '/mo', note: null, unavailable: true };
  return {
    amount: offer.amountCents / 100,
    // The unit is the real billing cadence. Annual is quoted as a yearly total because that
    // is the single charge that lands on the card; quoting it per month is how the old table
    // ended up describing $490 as "$49".
    unit: offer.interval === 'year' ? '/yr' : '/mo',
    note: offer.interval === 'year' ? 'billed annually' : null,
    priceBookKey: offer.priceBookKey,
  };
}

// ─── Identity (the only thing still read from the auth account) ───────────────
// Name, company and role genuinely live on the Supabase user, not in Stripe. `isFounding`
// is the metadata flag, which after SCRUM-46 governs the TRIAL allowance only - the price a
// founding member pays comes from their price book, served by the backend. Reading price
// from this flag is what produced the old hardcoded FOUNDING_PRICING table.
function identityFrom(user) {
  const m = user?.user_metadata || {};
  const name = m.full_name || [m.first_name, m.last_name].filter(Boolean).join(' ') || null;
  return {
    name,
    email: user?.email || null,
    company: m.company || null,
    role: m.role || null,
    isFounding: !!m.founding_member,
    memberSince: user?.created_at || null,
  };
}

/**
 * Trial state from the server's own per-user window.
 *
 * This used to be computed in the browser from a single global LAUNCH_DATE, so every user
 * saw the same trial dates regardless of when they signed up. The backend computes
 * max(signup, launch) + 7 days per user, and that is the window that actually gates the
 * product, so it is the one shown.
 */
function trialFrom(serverTrial, hasActiveSub) {
  const endsAt = serverTrial?.endsAt || null;
  const expired = !!serverTrial?.expired;
  if (!endsAt) return { status: 'unknown', startsAt: null, endsAt: null, daysLeft: 0, requiresCard: REQUIRES_CARD_FOR_TRIAL };
  const startsAt = new Date(new Date(endsAt).getTime() - TRIAL_DAYS * 86_400_000).toISOString();
  // A paid subscription supersedes the trial: showing "3 days left" to someone who has
  // already paid reads as though their access is about to stop.
  if (hasActiveSub) return { status: 'converted', startsAt, endsAt, daysLeft: 0, requiresCard: REQUIRES_CARD_FOR_TRIAL };
  if (expired) return { status: 'ended', startsAt, endsAt, daysLeft: 0, requiresCard: REQUIRES_CARD_FOR_TRIAL };
  if (Date.now() < new Date(startsAt).getTime()) {
    return { status: 'pending', startsAt, endsAt, daysLeft: TRIAL_DAYS, requiresCard: REQUIRES_CARD_FOR_TRIAL };
  }
  return { status: 'active', startsAt, endsAt, daysLeft: daysUntil(endsAt), requiresCard: REQUIRES_CARD_FOR_TRIAL };
}


// ─── Server-side credits (net-new verified leads) ─────────────────────────────
// The authoritative, cross-session balance lives in Supabase and is served by the
// backend. Requires the caller's JWT. Returns { available, allowance, used, remaining }
// or { available:false } when the token is missing / the credits schema isn't live yet.
async function fetchCredits(token) {
  if (!token) return { available: false };
  try {
    const res = await fetch(`${BACKEND_URL}/api/credits`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return { available: false };
    const d = await res.json();
    if (!d?.available) return { available: false };
    // Developers (DEV_EMAILS) come back as unlimited - no numeric balance.
    if (d.unlimited) return { available: true, unlimited: true };
    return {
      available: true,
      unlimited: false,
      allowance: d.allowance,
      used: d.used,
      remaining: d.remaining,
      boosterBalance: d.boosterBalance || 0,
      period: d.period || 'trial',
      periodEnd: d.periodEnd || null,
      // Other live Demo Week meters: { email_verifications: {allowance,used,remaining}, website_lookups: {...} }
      meters: d.meters || null,
    };
  } catch {
    return { available: false };
  }
}

// Canonical row metadata (label/icon/unit) per usage key, taken from the Solo catalogue.
// Used whenever a live meter needs to render for a user with NO plan selected (trial) -
// without this, those rows fell back to raw keys ("verify", "website") in the UI.
const ROW_META = Object.fromEntries(
  (PLAN_LIMITS.Solo || []).map((l) => [l.key, { label: l.label, icon: l.icon, unit: l.unit }])
);

// Maps a live usage_meters metric onto the Account "usage" row shape. `base` carries the
// row's label/icon/unit so a metric that isn't in the current plan's catalogue still
// renders sensibly.
function liveMeterRow(key, meter, base) {
  const { allowance, used, remaining } = meter;
  const booster = meter.boosterBalance || 0;
  return {
    key,
    label: base?.label || key,
    icon: base?.icon || 'activity',
    unit: base?.unit || '',
    used,
    limit: allowance,
    remaining,
    // Shown as its own line rather than folded into the total. Someone who has just paid
    // for a booster needs to see the thing they bought, not a number that is larger than
    // they remember; and because boosters never expire while the monthly allowance resets,
    // the two halves of the balance behave differently and are worth telling apart.
    boosterBalance: booster,
    unlimited: false,
    // Progress is against the MONTHLY allowance only. Including the booster would make the
    // bar read as though the plan were bigger than it is, and it would jump backwards at
    // the next reset when the booster is still there but the allowance has refilled.
    percent: allowance ? Math.min(100, Math.round((used / allowance) * 100)) : 0,
    live: true, // metering is real for this row (not "at launch")
  };
}

/**
 * The single badge at the top of the Account page, combining trial and subscription into
 * one word the reader can act on. Stripe's status wins when there is a subscription,
 * because that is what decides whether the card gets charged and whether access continues.
 *
 * Returns: 'active' | 'trialing' | 'past_due' | 'canceling' | 'canceled' | 'trial_ended' | 'inactive'
 */
function accountStatus(trial, hasActiveSub, stripeStatus) {
  if (hasActiveSub) {
    // past_due is deliberately surfaced rather than folded into 'active'. The card has
    // failed and access ends when the grace period does, so hiding it behind a green badge
    // would be the one thing the user most needs to know and cannot see.
    if (stripeStatus === 'past_due' || stripeStatus === 'unpaid') return 'past_due';
    if (stripeStatus === 'trialing') return 'trialing';
    if (stripeStatus === 'canceled') return 'canceled';
    return 'active';
  }
  if (trial.status === 'active') return 'trialing';
  if (trial.status === 'pending') return 'inactive';
  if (trial.status === 'ended') return 'trial_ended';
  return 'inactive';
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns the full, normalized account model for the Account page.
 * @param {object|null} user - the Supabase auth user (from useAuth()).
 * @param {{ token?: string }} [opts] - JWT for the authenticated credits read.
 */
export async function fetchBilling(user, { token } = {}) {
  const identity = identityFrom(user);

  // One authoritative read each. Neither is allowed to fail the page: a section that cannot
  // load says so, because a billing page that renders confident wrong numbers is worse than
  // one that admits it could not reach the server.
  const [sub, credits] = await Promise.all([
    fetchSubscription({ token }).catch(() => null),
    fetchCredits(token),
  ]);

  const server = sub?.subscription || null;          // our projection of the Stripe sub
  const hasActiveSub = !!server;
  const trial = trialFrom(sub?.trial, hasActiveSub);
  const priceBook = sub?.priceBook || { key: 'public', label: 'Standard', lifetimeLock: false };
  const offers = sub?.offers || {};

  // Plan and interval come from what Stripe is actually billing, never from what the user
  // picked at signup. Those disagree the moment anyone changes plan, and Stripe is right.
  const plan = server?.plan || null;
  const interval = server?.billingInterval === 'year' ? 'Annual'
    : server?.billingInterval === 'month' ? 'Monthly'
      : null;

  const status = accountStatus(trial, hasActiveSub, server?.status);

  // The price shown is the price being charged: the offer row matching the subscription's
  // own item key, at this user's price book. It is not looked up in a table here, so a
  // founding member cannot be shown a public rate (or the reverse).
  const itemKey = plan && interval
    ? `${plan.toLowerCase()}_${interval === 'Annual' ? 'yearly' : 'monthly'}`
    : null;
  const offer = itemKey ? offers[itemKey] : null;

  const subscription = server
    ? {
        plan,
        billing: interval || 'Monthly',
        isFounding: priceBook.lifetimeLock,
        priceBookLabel: priceBook.label,
        price: offer ? offer.amountCents / 100 : null,
        unit: interval === 'Annual' ? '/yr' : '/mo',
        priceNote: interval === 'Annual' ? 'billed annually' : null,
        currency: '$',
        status: server.status,                   // Stripe's own status verbatim
        accountStatus: status,
        canceled: !!server.cancelAtPeriodEnd || server.status === 'canceled',
        autoRenew: !server.cancelAtPeriodEnd && server.status !== 'canceled',
        pastDue: server.status === 'past_due',
        // The period end, stated once and unconditionally. nextBillingDate and cancelsAt
        // below are the same instant framed two ways (will you be charged, or does it stop),
        // and reading those to answer "when does access end" is how the cancel dialog came to
        // show a trial date that had already passed.
        periodEnd: server.currentPeriodEnd || null,
        billingStartsAt: server.currentPeriodStart || null,
        nextBillingDate: server.cancelAtPeriodEnd ? null : (server.currentPeriodEnd || null),
        cancelsAt: server.cancelAtPeriodEnd ? (server.currentPeriodEnd || null) : null,
        source: server.source || null,
      }
    : null;

  // ── Usage rows ─────────────────────────────────────────────────────────────
  // Built from the LIVE meters only. The old version seeded them from a plan catalogue and
  // a localStorage `usage` object, so a fresh account displayed a full set of allowances it
  // did not have, with 0 used against them.
  let usageOut = [];
  if (credits.available) {
    const leadsMeta = ROW_META.leads || { label: 'Lead credits', icon: 'user', unit: '' };
    usageOut.push(credits.unlimited
      ? { key: 'leads', ...leadsMeta, unlimited: true, live: true }
      : liveMeterRow('leads', {
        allowance: credits.allowance,
        used: credits.used,
        remaining: credits.remaining,
        boosterBalance: credits.boosterBalance,
      }, leadsMeta));

    const meterMap = {
      verify:  credits.meters?.email_verifications,
      enrich:  credits.meters?.company_enrichments,
      website: credits.meters?.website_lookups,
    };
    for (const [rowKey, meter] of Object.entries(meterMap)) {
      if (!meter || typeof meter.allowance !== 'number') continue;
      usageOut.push(liveMeterRow(rowKey, meter, ROW_META[rowKey] || { label: rowKey, icon: 'activity', unit: '' }));
    }

    // AI is never metered, on any plan. Showing it keeps all five categories of the pricing
    // model visible rather than leaving the reader to wonder what happened to it.
    const aiMeta = ROW_META.ai || { label: 'AI drafting & chat', icon: 'fileText', unit: '' };
    usageOut.push({ key: 'ai', ...aiMeta, unlimited: true, live: true });
  }

  return {
    identity: { ...identity, plan, billing: interval },
    trial,
    status,
    subscription,
    priceBook,
    offers,
    usage: usageOut,
    credits,
    // When the current allowance window resets. This is the ENTITLEMENT month, which for an
    // annual subscriber is not the billing date (they are charged once a year but refill
    // monthly), so it comes from the meter rather than from the invoice schedule.
    usageResetsAt: credits.available ? (credits.periodEnd || null) : null,
    // Real Stripe data. `billingAvailable:false` means Stripe could not be read, which the
    // UI must distinguish from "this account has no card".
    billingAvailable: sub ? sub.billingAvailable !== false : false,
    hasStripeCustomer: !!sub?.hasStripeCustomer,
    paymentMethods: sub?.paymentMethods || [],
    invoices: sub?.invoices || [],
    // False when the subscription read itself failed, so sections can show a retry rather
    // than an empty state that looks like a settled fact.
    loaded: !!sub,
    fetchedAt: new Date().toISOString(),
  };
}

// ─── Mutations ───────────────────────────────────────────────────────────────
//
// There is no local billing state left to mutate. Plan changes, interval changes,
// cancellation and resumption all happen in the Stripe Customer Portal, which is the only
// place they can actually take effect. Each of these used to write a flag to localStorage
// and return, so the UI showed "Canceled" while Stripe carried on charging the card.
//
// The Portal is configured per price book, so a founding member is only ever offered
// founding prices (see STRIPE-INTEGRATION.md W3).

/** Open the Stripe Customer Portal. Returns { ok, error } - the caller shows the error. */
export async function openBillingPortal({ token } = {}) {
  return portalAndRedirect({ token });
}

// Kept as named exports because the Account page and its sections import them by name.
// All four are now the same action: hand the user to the Portal.
export const changePlan = (_user, _plan, opts) => openBillingPortal(opts);
export const changeBilling = (_user, _billing, opts) => openBillingPortal(opts);
export const cancelSubscription = (_user, opts) => openBillingPortal(opts);
export const resumeSubscription = (_user, opts) => openBillingPortal(opts);
